import React, { useState, useEffect } from 'react';
import { FaCheck, FaSpinner } from 'react-icons/fa';

const AddressFields = ({ address, handleChange, countries, states, loadingStates, setIsContinueDisabled }) => {
  const [errors, setErrors] = useState({});
  const [showPinOnly, setShowPinOnly] = useState(true);
  const [isFetchingPin, setIsFetchingPin] = useState(false);

  const fields = [
    { name: 'fullName', label: 'Full Name', type: 'text', required: true },
    { name: 'phone', label: 'Phone', type: 'tel', required: true },
    { name: 'house', label: 'House Name', type: 'text', required: true },
    { name: 'number', label: 'House No', type: 'text', required: true },
    { name: 'street', label: 'Street', type: 'text', required: true },
    { name: 'district', label: 'District', type: 'text', required: true },
    { name: 'city', label: 'City', type: 'text', required: true },
    {
      name: 'country',
      label: 'Country',
      type: 'select',
      required: true,
      options: [{ code: '', name: 'Select a country' }, ...countries],
      renderOption: (opt) => <option key={opt.code} value={opt.code}>{opt.name}</option>,
    },
    {
      name: 'state',
      label: 'State',
      type: 'select',
      required: true,
      disabled: !address.country || loadingStates || !states.length,
      options: loadingStates
        ? [{ code: '', name: 'Loading states...' }]
        : [{ code: '', name: 'Select a state' }, ...states],
      renderOption: (opt) => <option key={opt.code} value={opt.code}>{opt.name}</option>,
    },
    { name: 'postalCode', label: 'Postal Code', type: 'text', required: true },
    {
      name: 'reference',
      label: 'Reference (Optional)',
      type: 'text',
      required: false,
      colSpan: 'md:col-span-2',
    },
  ];

  const validateField = (name, value, required) => {
    if (required && !value?.trim()) {
      return `${name.charAt(0).toUpperCase() + name.slice(1).replace(/([A-Z])/g, ' $1')} is required`;
    }
    return '';
  };

  const validateAllFields = () => {
    const requiredFields = fields.filter((f) => f.required).map((f) => f.name);
    return requiredFields.every((field) => address[field]?.trim());
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const field = fields.find((f) => f.name === name);
    if (field) {
      const error = validateField(name, value, field.required);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleInputChange = (e) => {
    handleChange(e);
    const { name, value } = e.target;
    const field = fields.find((f) => f.name === name);
    if (field) {
      const error = validateField(name, value, field.required);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
    // Update button state
    setIsContinueDisabled(!validateAllFields());
  };

  const fetchPinDetails = async () => {
    const pinCode = address.postalCode;
    const countryCode = address.country || 'IN';

    if (!pinCode || !/^\d{4,10}$/.test(pinCode)) {
      setErrors((prev) => ({
        ...prev,
        postalCode: 'Invalid PIN Code',
      }));
      setIsContinueDisabled(true); // Disable button on invalid PIN
      return;
    }

    setIsFetchingPin(true);
    try {
      const response = await fetch(`https://geocodes.envia.com/zipcode/${countryCode}/${pinCode}`);
      const data = await response.json();
      console.log('Envia ZIP Response:', data);

      const zipInfo = Array.isArray(data) ? data[0] : null;

      if (zipInfo && zipInfo.zip_code) {
        const updatedFields = {
          street: zipInfo.suburbs?.[0] || address.street || '',
          district: zipInfo.regions?.region_3 || address.district || '',
          city: zipInfo.locality || address.city || '',
          state: zipInfo.state?.code?.['2digit'] || address.state || '',
          country: zipInfo.country?.code || countryCode,
        };

        Object.entries(updatedFields).forEach(([name, value]) => {
          handleChange({
            target: { name, value },
          });
          setErrors((prev) => ({ ...prev, [name]: '' }));
        });

        setShowPinOnly(false);
        setErrors((prev) => ({ ...prev, postalCode: '' }));
        // Enable button if all required fields are filled
        setIsContinueDisabled(!validateAllFields());
      } else {
        setErrors((prev) => ({
          ...prev,
          postalCode: 'Invalid PIN code',
        }));
        setIsContinueDisabled(true);
      }
    } catch (error) {
      console.error('Envia ZIP Error:', error);
      setErrors((prev) => ({
        ...prev,
        postalCode: 'Try again',
      }));
      setIsContinueDisabled(true);
    } finally {
      setIsFetchingPin(false);
    }
  };

  // Validate fields on address change
  useEffect(() => {
    setIsContinueDisabled(!validateAllFields());
  }, [address, setIsContinueDisabled]);

  // Initial validation on mount
  useEffect(() => {
    const initialErrors = {};
    fields.forEach(({ name, required }) => {
      const value = address[name] || '';
      const error = validateField(name, value, required);
      if (error) initialErrors[name] = error;
    });
    setErrors(initialErrors);
    setIsContinueDisabled(!validateAllFields());
  }, [address, countries, states, setIsContinueDisabled]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 mb-2 gap-6 w-full max-w-full overflow-x-auto">
      {showPinOnly ? (
        <div className="min-w-0 relative">
          <label className="block text-sm font-medium text-[#DBAE6E] mb-1">Enter your PIN Code</label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              name="postalCode"
              value={address.postalCode || ''}
              onChange={handleInputChange}
              onBlur={handleBlur}
              required
              className={`w-full p-3 mb-8 h-full bg-primary dark:bg-primary-dark border ${
                errors.postalCode ? 'border-red-500' : 'border-[#DBAE6E]'
              } text-black dark:text-white rounded-lg focus:outline-none focus:ring-2 ${
                errors.postalCode ? 'focus:ring-red-500' : 'focus:ring-[#DBAE6E]'
              } box-border`}
            />
            <button
              onClick={fetchPinDetails}
              disabled={isFetchingPin}
              className={`p-3 rounded-lg flex mb-8 items-center justify-center ${
                isFetchingPin ? 'bg-gray-600' : 'bg-[#DBAE6E] hover:bg-[#c89e5d]'
              } text-black transition-colors duration-200`}
            >
              {isFetchingPin ? (
                <FaSpinner className="animate-spin text-xl" />
              ) : (
                <div className="flex ">
                <FaCheck className="text-xl" /> 
                <p className='px-2'>Verify</p>
                </div>
           
              )}
            </button>
          </div>
          {errors.postalCode && (
            <span className="absolute top-[70px] left-3 text-red-500 text-xs bg-primary dark:bg-primary-dark px-1 rounded">
              {errors.postalCode}
            </span>
          )}
        </div>
      ) : (
        fields.map((field) => (
          <div key={field.name} className={`${field.colSpan || ''} min-w-0 relative`}>
            <label className="block text-sm font-medium text-[#DBAE6E] mb-1">{field.label}</label>
            {field.type === 'select' ? (
              <select
                name={field.name}
                value={address[field.name] || ''}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required={field.required}
                disabled={field.disabled}
                className={`w-full p-3 bg-primary dark:bg-primary-dark border ${
                  errors[field.name] ? 'border-red-500' : 'border-[#DBAE6E]'
                } text-black dark:text-white rounded-lg focus:outline-none focus:ring-2 ${
                  errors[field.name] ? 'focus:ring-red-500' : 'focus:ring-[#DBAE6E]'
                } box-border appearance-none`}
              >
                {field.options.map(field.renderOption)}
              </select>
            ) : (
              <input
                type={field.type}
                name={field.name}
                value={address[field.name] || ''}
                onChange={handleInputChange}
                onBlur={handleBlur}
                required={field.required}
                className={`w-full p-3 bg-primary dark:bg-primary-dark border ${
                  errors[field.name] ? 'border-red-500' : 'border-[#DBAE6E]'
                } text-black dark:text-white rounded-lg focus:outline-none focus:ring-2 ${
                  errors[field.name] ? 'focus:ring-red-500' : 'focus:ring-[#DBAE6E]'
                } box-border`}
              />
            )}
            {errors[field.name] && (
              <span className="absolute top-[70px] left-3 text-red-500 text-xs bg-primary dark:bg-primary-dark px-1 rounded">
                {errors[field.name]}
              </span>
            )}
          </div>
        ))
      )}
    </div>
  );
};

export default AddressFields;